import { useMemo } from "react";
import PropTypes from "prop-types";
import Typography from "@mui/material/Typography";
import FilterablePaginatedTable from "common/Components/FilterablePaginatedTable";
import {
  getPlatformUserListRowAction,
  isPlatformUserEmailClickable,
  mapPlatformListFiltersToColumnFilters,
  PLATFORM_USER_ACCESS_ACCOUNT_STATUS_FILTER_OPTIONS,
  PLATFORM_USER_ACCESS_LAYOUT,
  PLATFORM_USER_ACCESS_UI,
} from "../PlatformUserAccess.constants";
import { AccessStatusChip } from "common/Components/GenericForm/FieldTypes/AccessStatusSelect";
import {
  TableRowActionCell,
  TableRowActionLink,
  TableStatusCell,
  UsernameActionButton,
} from "./UserAccessList.styles";

const displayCell = (value) => {
  const text = String(value ?? "").trim();
  return text || "—";
};

const UserAccessList = ({
  rows,
  loading = false,
  pagination,
  listFilters,
  onListFiltersChange,
  platformRoleFilterOptions,
  onPageChange,
  onRowsPerPageChange,
  onRowClick,
  onRowAction,
  toolbarActions,
}) => {
  const columns = [
    {
      id: "email",
      label: PLATFORM_USER_ACCESS_UI.tableColumns.email,
      sortable: false,
      filter: { type: "text" },
      render: (value, row) => {
        const label = displayCell(value);
        if (!isPlatformUserEmailClickable(row.status)) {
          return (
            <Typography component="span" variant="body2">
              {label}
            </Typography>
          );
        }
        return (
          <UsernameActionButton variant="text" onClick={() => onRowClick(row.id)}>
            {label}
          </UsernameActionButton>
        );
      },
    },
    {
      id: "firstName",
      label: PLATFORM_USER_ACCESS_UI.tableColumns.firstName,
      sortable: false,
      filter: { type: "text" },
      render: (value) => displayCell(value),
    },
    {
      id: "lastName",
      label: PLATFORM_USER_ACCESS_UI.tableColumns.lastName,
      sortable: false,
      filter: { type: "text" },
      render: (value) => displayCell(value),
    },
    {
      id: "phone",
      label: PLATFORM_USER_ACCESS_UI.tableColumns.phone,
      sortable: false,
      filter: { type: "text" },
      render: (value) => displayCell(value),
    },
    {
      id: "platformRole",
      label: PLATFORM_USER_ACCESS_UI.tableColumns.platformRole,
      sortable: false,
      filter: {
        type: "select",
        options: platformRoleFilterOptions ?? [],
      },
    },
    {
      id: "status",
      label: PLATFORM_USER_ACCESS_UI.tableColumns.accountStatus,
      sortable: false,
      filter: {
        type: "select",
        options: PLATFORM_USER_ACCESS_ACCOUNT_STATUS_FILTER_OPTIONS,
      },
      render: (value) => (
        <TableStatusCell>
          <AccessStatusChip status={value} />
        </TableStatusCell>
      ),
    },
    {
      id: "action",
      label: PLATFORM_USER_ACCESS_UI.tableColumns.action,
      sortable: false,
      render: (_, row) => {
        const rowAction = getPlatformUserListRowAction(row.status);
        if (!rowAction || typeof onRowAction !== "function") {
          return null;
        }
        return (
          <TableRowActionCell onClick={(event) => event.stopPropagation()}>
            <TableRowActionLink
              type="button"
              onClick={() => onRowAction(row, rowAction.action)}
            >
              {rowAction.label}
            </TableRowActionLink>
          </TableRowActionCell>
        );
      },
    },
  ];

  const tableFilterValues = useMemo(
    () => mapPlatformListFiltersToColumnFilters(listFilters),
    [listFilters]
  );

  return (
    <FilterablePaginatedTable
      columns={columns}
      rows={rows}
      loading={loading}
      getRowId={(row) => row.id}
      showExport={false}
      serverMode
      page={pagination.page}
      totalRows={pagination.totalCount}
      defaultRowsPerPage={pagination.size}
      rowsPerPageOptions={PLATFORM_USER_ACCESS_LAYOUT.tableRowsPerPageOptions}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      onFilterChange={onListFiltersChange}
      filterValues={tableFilterValues}
      applyTextFiltersOnCommit
      toolbarActions={toolbarActions}
    />
  );
};

UserAccessList.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  pagination: PropTypes.shape({
    page: PropTypes.number.isRequired,
    totalCount: PropTypes.number.isRequired,
    size: PropTypes.number.isRequired,
  }).isRequired,
  listFilters: PropTypes.object,
  onListFiltersChange: PropTypes.func.isRequired,
  platformRoleFilterOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  onRowClick: PropTypes.func.isRequired,
  onRowAction: PropTypes.func.isRequired,
  toolbarActions: PropTypes.node,
};

UserAccessList.defaultProps = {
  loading: false,
  platformRoleFilterOptions: [],
};

export default UserAccessList;
